import { computed, ref } from 'vue';
import type { ComputedRef, Ref } from 'vue';

import type { DepartureCity } from './types';
import { sortCities } from '../lib/sortCities';
import { DEPARTURE_CITY_TEXTS } from '../config/texts';

export interface UseDepartureCity {
  inputString: Ref<string>;
  selection: Ref<DepartureCity | null>;
  controlActivated: Ref<boolean>;
  invalid: Ref<boolean>;
  errorText: Ref<string>;

  optionsList: ComputedRef<DepartureCity[]>;
  label: ComputedRef<string>;
  clearVisible: ComputedRef<boolean>;

  open: () => void;
  close: () => void;
  setInput: (v: string) => void;
  selectCity: (city: DepartureCity) => void;
  clear: () => void;
  validate: () => boolean;
  restore: (city: DepartureCity | null) => void;
  setCities: (list: DepartureCity[]) => void;
}

export interface UseDepartureCityOptions {
  initialCities?: DepartureCity[];
}

export function useDepartureCity(options: UseDepartureCityOptions = {}): UseDepartureCity {
  const inputString = ref('');
  const selection = ref<DepartureCity | null>(null);
  const controlActivated = ref(false);
  const invalid = ref(false);
  const errorText = ref('');
  const cities = ref<DepartureCity[]>(options.initialCities ?? []);

  const optionsList = computed<DepartureCity[]>(() => {
    const sorted = sortCities(cities.value);
    const query = inputString.value.trim().toLowerCase();
    if (query === '') {
      return sorted;
    }
    return sorted.filter((c) => c.name.toLowerCase().includes(query));
  });

  const label = computed<string>(() => selection.value?.name ?? '');

  const clearVisible = computed<boolean>(() => controlActivated.value && inputString.value !== '');

  const open = (): void => {
    controlActivated.value = true;
    inputString.value = '';
  };

  const close = (): void => {
    controlActivated.value = false;
    invalid.value = false;
    errorText.value = '';
    inputString.value = label.value;
  };

  const setInput = (v: string): void => {
    inputString.value = v;
  };

  const selectCity = (city: DepartureCity): void => {
    selection.value = city;
    inputString.value = city.name;
    controlActivated.value = false;
    invalid.value = false;
    errorText.value = '';
  };

  const clear = (): void => {
    inputString.value = '';
    invalid.value = false;
    errorText.value = '';
  };

  const validate = (): boolean => {
    if (selection.value === null) {
      invalid.value = true;
      errorText.value = DEPARTURE_CITY_TEXTS.errorEmpty;
      return false;
    }
    invalid.value = false;
    errorText.value = '';
    return true;
  };

  const restore = (city: DepartureCity | null): void => {
    selection.value = city;
    inputString.value = city?.name ?? '';
    controlActivated.value = false;
  };

  const setCities = (list: DepartureCity[]): void => {
    cities.value = list;
  };

  return {
    inputString,
    selection,
    controlActivated,
    invalid,
    errorText,
    optionsList,
    label,
    clearVisible,
    open,
    close,
    setInput,
    selectCity,
    clear,
    validate,
    restore,
    setCities,
  };
}
